// src/unregisterServiceWorker.ts

/**
 * This project previously included a full Create React App–style
 * service worker registration setup (registerServiceWorker.ts).
 *
 * That setup was designed to:
 * - register a service worker in production
 * - enable offline support and aggressive asset caching
 * - handle update lifecycle ("new content available on next visit")
 *
 * This logic is no longer needed because:
 * - The app is built with Vite, which does NOT generate or register
 *   a service worker by default.
 * - This is an admin application, where immediate updates after deploy
 *   are preferred over offline-first caching.
 * - Service workers can cause stale UI issues in admin tools.
 *
 * Instead of registering a service worker, we explicitly unregister
 * any existing ones that might still be present in users' browsers
 * from older deployments or previous setups.
 *
 * This ensures:
 * - No stale caches or unexpected offline behavior
 * - A clean transition away from PWA / service worker usage
 * - Users always receive the latest deployed assets
 */

export async function unregisterServiceWorker() {
  // Guard: service workers are not supported in all environments
  if (!('serviceWorker' in navigator)) return;

  // Get all service worker registrations for this origin
  const registrations = await navigator.serviceWorker.getRegistrations();

  // Unregister every service worker found
  // (usually zero, but this safely cleans up any leftovers)
  await Promise.all(registrations.map((reg) => reg.unregister()));
}
